import React, { useMemo } from "react"
import { APIAppTemplateModel } from "../../../../../../Models/API/AppTemplateModel"
import {
    APIAppInstanceModel,
    isAppInstanceArchived,
    mapAppInstanceStatus
} from "../../../../../../Models/API/AppInstanceModel"
import { Box, Checkbox, Panel, Text } from "dash-styled"
import { fromNow } from "../../../../../../Helpers/Functions/dateFormat"
import { getValue, isLoading } from "../../../../../../Models/Client/FetchState"
import { JobsProgressChart } from "../../../Common/Statistics/JobsProgressChart"
import { ErrorInstanceTag } from "./ErrorInstanceTag"
import { hasAppPermission } from "../../../../../../Models/Client/ProjectRole"
import { useJobCount } from "../../../../../../Hooks/useJobCount"
import { APIJobsPerStatusModel } from "../../../../../../Models/API/APIJobsPerStatusModel"
import upperFirst from "lodash/upperFirst"
import { useAppState } from "../../../../../../Hooks/useReduxState"
import { selectHasFreemiumExpired } from "../../../../../../Helpers/Functions/Selectors/auth"
import { selectAssumedRoles } from "../../../../../../Helpers/Functions/Selectors/collaborator"

interface InstanceCardProps {
    onItemClick?: () => void
    onStatsClick?: () => void
    template?: APIAppTemplateModel
    instance: APIAppInstanceModel
    onToggleCheck?: (item: APIAppInstanceModel) => void
    errorMessage?: string
    isChecked?: boolean
}

export const InstanceCard = ({
    template,
    instance,
    onItemClick,
    onStatsClick,
    onToggleCheck,
    errorMessage,
    isChecked
}: InstanceCardProps) => {
    const assumedRoleFetch = useAppState(state =>
        selectAssumedRoles(state, instance.uuid)
    )
    const assumedRole = getValue(assumedRoleFetch)

    const hasFreemiumPlanExpired = useAppState(selectHasFreemiumExpired)

    errorMessage =
        errorMessage ??
        (assumedRole === undefined && !isLoading(assumedRoleFetch)
            ? "Missing permission"
            : errorMessage)

    const handleToggleCheck = useMemo(
        () =>
            onToggleCheck === undefined ||
            !hasAppPermission(assumedRole ?? [], "app:archive")
                ? undefined
                : (event: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
                      event.stopPropagation()
                      onToggleCheck(instance)
                  },
        [assumedRole, instance, onToggleCheck]
    )

    const handleItemClick = (event: React.SyntheticEvent) => {
        event.preventDefault()
        event.stopPropagation()
        onItemClick?.()
    }

    const handleStatsClick = (event: React.SyntheticEvent) => {
        event.preventDefault()
        event.stopPropagation()
        onStatsClick?.()
    }

    const isAppArchived = isAppInstanceArchived(instance)

    return (
        <Panel
            noPadding={true}
            size="sm"
            className={isAppArchived ? "bg-smoke-320 shadow-none" : ""}
        >
            <div
                className={`flex flex-grow items-center pl-4 py-4 gap-4 ${
                    onItemClick ? "cursor-pointer" : ""
                }`}
                onClick={handleItemClick}
            >
                <div className="w-7/12 flex-grow flex-shrink">
                    <div className="flex items-center">
                        <div onClick={handleToggleCheck}>
                            <Checkbox
                                title="Select an app"
                                isChecked={isChecked}
                                disabled={handleToggleCheck === undefined}
                            />
                        </div>
                        <Text
                            className="m-0 text-rhino-900"
                            weight="semibold"
                            size="lg"
                            testId="project-name"
                        >
                            <button className="font-semibold p-0">
                                {instance.name}
                            </button>
                            <Text
                                color={
                                    hasFreemiumPlanExpired ? "red" : "coldgreen"
                                }
                                component="span"
                                weight="regular"
                                className={`inline-flex m-0 ml-2 items-center font-normal ${
                                    isAppArchived ? "text-rhino-900" : ""
                                }`}
                                size="sm"
                            >
                                <Text
                                    component="span"
                                    weight="semibold"
                                    size="lg"
                                    color="currentColor"
                                >
                                    •
                                </Text>

                                {upperFirst(
                                    hasFreemiumPlanExpired
                                        ? "Trial expired"
                                        : mapAppInstanceStatus(
                                              instance.status
                                          ).toLocaleLowerCase()
                                )}
                            </Text>
                        </Text>
                    </div>
                    <div className="flex mt-1">
                        <Checkbox className="invisible m-0" />

                        <Text
                            size="xs"
                            color="gray"
                            component="div"
                            className="text-smoke-610"
                        >
                            {template ? template.metadata.name + " | " : ""}
                            {`Created ${fromNow(instance.created)}`}
                        </Text>
                    </div>
                </div>
                <Stats instance={instance} onClick={handleStatsClick} />
                <div className="flex-grow-0 flex-shrink-0 w-20 flex flex-col gap-1 items-stretch text-center text-black text-role-tag text-opacity-60">
                    {instance.role !== null && (
                        <div className="capitalize bg-smoke-320 py-1">
                            {instance.role.toLocaleLowerCase()}
                        </div>
                    )}
                    {instance.workerRole !== null && (
                        <div className="capitalize bg-smoke-320 py-1">
                            {instance.workerRole.toLocaleLowerCase()}
                        </div>
                    )}
                </div>
            </div>
            {errorMessage && (
                <ErrorInstanceTag
                    message={errorMessage}
                    className={isAppArchived ? "bg-smoke-890" : ""}
                />
            )}
        </Panel>
    )
}

interface StatsProps {
    instance: APIAppInstanceModel
    onClick: (event: React.SyntheticEvent) => void
}

const Stats = ({ instance, onClick }: StatsProps) => {
    const count = useJobCount(instance.uuid)
    const total = useMemo(
        () => (count === undefined ? 0 : APIJobsPerStatusModel.total(count)),
        [count]
    )

    if (count === undefined) {
        return null
    }

    return (
        <Box display="flex" flexDirection="column" className="w-1/3 mb-4">
            <div className="flex flex-col items-end flex-1" onClick={onClick}>
                <Text
                    className="m-0 text-rhino-900 mb-2"
                    size="sm"
                    testId="project-job-count"
                >
                    {String(total) + ` data point${total === 1 ? "" : "s"}`}
                </Text>
                <div
                    className="w-full max-w-xs rounded-full overflow-hidden"
                    style={{ height: "10px" }}
                >
                    <JobsProgressChart total={total} data={count} />
                </div>
            </div>
        </Box>
    )
}
